'use client';

import { tv } from '../_lib/utils';
import { Container } from '../Container';
import { type CSSProperties, Fragment, useMemo } from 'react';
import { Text } from '../Text';
import type { brandValues } from '../_lib/designTypesStore';
import { CardSectionHeader, type CardSectionHeaderProps } from '../CardSectionHeader';
import { useThemeValue, type Themes } from '../Theme';
import { Anchor } from '../Anchor';
import { IconResolver } from '../IconResolver';

const styles = tv({
	slots: {
		table: 'relative grid items-stretch md:grid-cols-(--col-count)',
		headerCell: 'flex pb-spacing-3xs pr-spacing-3xs md:pb-spacing-md md:pr-spacing-md',
		cardContent:
			'relative w-full p-3 flex flex-col gap-0.5 rounded-sm bg-nav-secondary-item-background-brand border border-nav-secondary-item-border-brand transition-colors duration-fast empty:invisible md:p-6',
		cell: 'max-w-max-md py-spacing-3xs pr-spacing-3xs md:py-spacing-lg md:pr-spacing-md',
		border: 'col-span-full bg-border-primary h-px mt-spacing-xs md:mt-0',
	},
	variants: {
		size: {
			medium: {
				table: 'grid-cols-(--medium-col-count)',
			},
			large: {
				table: 'grid-cols-(--large-col-count) max-md:overflow-auto max-md:-mx-(--container-padding) max-md:px-(--container-padding)',
			},
		},
		leading: {
			true: {
				headerCell: 'max-md:hidden',
				cell: 'mt-spacing-xs max-md:col-span-full md:mt-0',
			},
			false: {},
		},
	},
	compoundVariants: [
		{
			size: 'large',
			leading: true,
			className: {
				cell: 'max-md:w-fit max-md:sticky max-md:left-(--container-padding) max-md:-translate-x-(--container-padding)',
			},
		},
	],
});

export type LargeTableCell = {
	text?: string;
	colSpan?: number;
};

export type LargeTableRow = {
	name?: string;
	subText?: string;
	cells: LargeTableCell[];
};

type HeaderBlank = {
	type: 'blank';
};

type HeaderText = {
	type: 'text';
	name?: string;
	subText?: string;
};

type HeaderCard = {
	type: 'card';
	name?: string;
	subText?: string;
	href?: string;
	brand?: (typeof brandValues)[number];
	showExternalIcon?: boolean;
};

export type LargeTableProps = {
	defaultTheme?: Themes;
	className?: string;
	header?: Omit<CardSectionHeaderProps, 'className'>;
	headerCells?: Array<HeaderBlank | HeaderText | HeaderCard>;
	rows?: LargeTableRow[];
};

export function LargeTable(props: LargeTableProps) {
	const theme = props.defaultTheme ?? useThemeValue();

	const rows = props.rows ?? [];
	const colCount = useMemo(
		() => Math.max(...rows.map(row => row.cells?.reduce((acc, cell) => acc + (cell.colSpan ?? 1), 0) ?? 0)) + 1,
		[props.rows],
	);

	const size: 'medium' | 'large' = colCount > 3 ? 'large' : 'medium';

	const { table, headerCell, cardContent, cell: cellStyles, border } = styles({ size });

	return (
		<section className={props.className} data-component='LargeTable'>
			<Container className='flex flex-col gap-responsive-2xl'>
				{props.header && <CardSectionHeader {...props.header} />}

				<div
					className={table()}
					style={
						{
							'--col-count': `repeat(${colCount}, minmax(0, 1fr))`,
							'--medium-col-count': `repeat(${colCount - 1}, minmax(0, 1fr))`,
							'--large-col-count': `repeat(${colCount - 1}, minmax(50%, 1fr))`,
						} as CSSProperties
					}
				>
					{props.headerCells?.map((cell, i) => {
						switch (cell.type) {
							case 'blank':
								return <div key={`${cell.type}-${i}-large-table-header-cell`} className={headerCell({ leading: i === 0 })} />;
							case 'text':
								return (
									<div key={`${cell.type}-${i}-large-table-header-cell`} className={headerCell({ leading: i === 0 })}>
										<div className='flex flex-col gap-0.5'>
											{cell.name && (
												<Text className='text-text-primary' styleType='heading-3xs'>
													{cell.name}
												</Text>
											)}

											{cell.subText && (
												<Text className='text-text-secondary' styleType='body-sm'>
													{cell.subText}
												</Text>
											)}
										</div>
									</div>
								);
							case 'card':
								return (
									// data-theme={theme} needs to be used here as otherwise data-brand won't work due to the way css variables get resolved
									<div
										key={`${cell.type}-${i}-large-table-header-cell`}
										className={headerCell({ leading: i === 0 })}
										data-brand={cell.brand}
										data-theme={theme}
									>
										<div className={`${cell.href ? 'hover:bg-nav-secondary-item-background-brand-hover' : ''} ${cardContent()}`}>
											{cell.name &&
												(cell.href ? (
													<Text styleType='heading-3xs'>
														<Anchor
															className="flex gap-2 before:content-[''] before:bottom-0 before:left-0 before:right-0 before:top-0 before:absolute"
															href={cell.href}
														>
															{cell.name}
															<IconResolver
																className='text-text-brand shrink-0 w-4 h-4'
																icon={cell.showExternalIcon ? 'arrow-square-out' : 'arrow-up-right'}
															/>
														</Anchor>
													</Text>
												) : (
													<Text className='text-text-primary' styleType='heading-3xs'>
														{cell.name}
													</Text>
												))}

											{cell.subText && (
												<Text className='text-text-secondary' styleType='body-sm'>
													{cell.subText}
												</Text>
											)}
										</div>
									</div>
								);
						}
					})}

					{rows.map((row, i) => (
						<Fragment key={`${i}-${row.name?.slice(0, 10)}`}>
							<div className={border()} />

							<div className={`flex flex-col gap-2 ${cellStyles({ leading: true })}`}>
								{row.name && (
									<Text className='text-text-primary' styleType='heading-3xs'>
										{row.name}
									</Text>
								)}

								{row.subText && (
									<Text className='text-text-secondary' styleType='body-sm'>
										{row.subText}
									</Text>
								)}
							</div>

							{row.cells.map((cell, j) => (
								<Text
									key={`${cell.text}-${j}-large-table-cell`}
									className={`text-text-secondary ${cellStyles()}`}
									style={{ gridColumn: cell.colSpan ? `span ${cell.colSpan} / span ${cell.colSpan}` : undefined }}
									styleType='body-lg-medium'
								>
									{cell.text}
								</Text>
							))}
						</Fragment>
					))}
				</div>
			</Container>
		</section>
	);
}
